import { useCallback } from 'react';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { fromWei, toWei } from '@sorare/core/src/lib/wei';

const algoliaWeiFactor = BigInt(1e13);

type ReturnedProps = {
  algoliaWeiFactor: bigint;
  formatPrice: (value: number, maxReachesEnd?: boolean) => string;
  sliderToPrice: (value: number) => string;
  priceToSlider: (value: number) => number;
};

export const useFormatFilters = (): ReturnedProps => {
  const { currency, fiatCurrency } = useCurrentUserContext();
  const { wei, toMonetaryAmountInFiatCurrency, getUserFiatAmountInMajorUnit } =
    useMonetaryAmount();
  const { formatEth, formatNumber } = useIntlContext();

  const sliderToPrice = useCallback(
    (value: number): string => {
      const monetaryAmount = wei(Math.round(value * Number(algoliaWeiFactor)));

      if (currency === Currency.FIAT) {
        return getUserFiatAmountInMajorUnit(monetaryAmount).toFixed(2);
      }
      return fromWei(monetaryAmount.wei).toString();
    },
    [currency, wei, getUserFiatAmountInMajorUnit]
  );

  const formatPrice = useCallback(
    (value: number, maxReachesEnd?: boolean) => {
      const price = sliderToPrice(value);

      let formattedNumber;
      if (currency === Currency.FIAT) {
        formattedNumber = formatNumber(Number(price), {
          style: 'currency',
          currency: fiatCurrency.code,
        });
      } else {
        formattedNumber = formatEth(price);
      }

      return `${formattedNumber}${maxReachesEnd ? '+' : ''}`;
    },
    [currency, fiatCurrency.code, formatEth, formatNumber, sliderToPrice]
  );

  const priceToSlider = useCallback(
    (value: number): number => {
      let weiAmount;
      if (currency === Currency.FIAT) {
        weiAmount = toMonetaryAmountInFiatCurrency(value).wei;
      } else {
        weiAmount = toWei(value);
      }

      return Number(weiAmount / algoliaWeiFactor);
    },
    [currency, toMonetaryAmountInFiatCurrency]
  );

  return {
    algoliaWeiFactor,
    formatPrice,
    sliderToPrice,
    priceToSlider,
  };
};
